import React, {useEffect, useState} from "react"
// mui
import {
    Backdrop, Box, Button, Collapse, CircularProgress,
    FormControl, Grid2 as Grid, IconButton, InputLabel, MenuItem, Select, Skeleton, TextField, Typography, Stack,
} from "@mui/material";
import {Add, Check, Close, MonitorHeartRounded} from "@mui/icons-material";
// translation
import {useTranslation} from "react-i18next";
// data
import {useData} from "@/context/PackagingDataProvider";
import {useUser} from "@/context/UserProvider";
import {usePermissions} from "@/context/PermissionsProvider";
// layout
import {PageHeader, SectionCard, Badge} from "@/components/Layout";
import {packagingTokens as tk} from "@/theme/packagingTokens";

export default function HealthchecksTab() {

    const {t} = useTranslation(["healthchecks", "common"])
    const {toggleHealthchecks, updateHealthchecks, getHealthchecksStatus, commonFilters} = useData()
    const {organisation} = useUser()
    const {isAccountRole} = usePermissions()

    // Healthchecks management is OWNER/ADMIN/SUPERVISOR. Members and analysts
    // see the status but cannot modify anything (negative check so a
    // standalone account with no org role keeps access — the tabs' convention).
    const readOnlyRole = isAccountRole(["member", "analyst"])
    // A SUPERVISOR manages healthchecks for their own organisation only:
    // with another organisation selected in the sidebar, the tab goes
    // read-only rather than acting outside their scope.
    const isSupervisor = isAccountRole(["supervisor"])
    const selectedOrg = commonFilters?.organisation ?? ""
    const onOwnOrganisation = !selectedOrg || selectedOrg === organisation?.organisation_id
    const canManage = !readOnlyRole && (!isSupervisor || onOwnOrganisation)

    const [activeHC, setActiveHC] = useState<boolean>(false)
    const [activeSettings, setActiveSettings] = useState<any>(null)
    const [healthchecksSettings, setHealthchecksSettings] = useState<any>({
        enabled: false, interval: 1440, retries: 1, retryInterval: 1, notifyAddresses: []
    })
    const [pendingEmail, setPendingEmail] = useState<string>("")
    const [loading, setLoading] = useState<boolean>(false)
    // First status fetch in flight — render skeletons instead of a misleading
    // "inactive" flash while we don't know the real state yet.
    const [initializing, setInitializing] = useState<boolean>(true)

    useEffect(() => {
        checkIfHCAreActive().finally(() => setInitializing(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkIfHCAreActive = async () => {
        await getHealthchecksStatus().then((res: any) => {
            if (res === "no healthchecks") {
                setActiveHC(false)
                setActiveSettings(null)
                return
            }
            setHealthchecksSettings(res)
            setActiveSettings(res)
            setActiveHC(true)
        })
    }

    const handleToggleHealthchecks = () => {
        if (!canManage) return
        setLoading(true)
        if (healthchecksSettings?.owner) {
            if (activeSettings?.enabled) toggleHealthchecks("disable", healthchecksSettings)
            else toggleHealthchecks("enable", healthchecksSettings)
        } else {
            toggleHealthchecks("setup")
        }
        setTimeout(() => { checkIfHCAreActive().then(); setLoading(false) }, 1500)
    }

    const handleUpdateHealthchecks = () => {
        if (!canManage) return
        setLoading(true)
        updateHealthchecks(healthchecksSettings)
        setTimeout(() => { checkIfHCAreActive().then(); setLoading(false) }, 1500)
    }

    const addEmailToList = () => {
        let list = healthchecksSettings.notifyAddresses
        list.push(pendingEmail)
        setPendingEmail("")
        setHealthchecksSettings({...healthchecksSettings, notifyAddresses: list})
    }

    const removeEmailFromList = (email: string) => {
        let list = healthchecksSettings.notifyAddresses
        if (!!list && list.length > 1) list = list.filter((add: string) => add !== email)
        else return
        setHealthchecksSettings({...healthchecksSettings, notifyAddresses: list})
    }

    const isEnabled = !!activeSettings?.enabled
    const hasChanges = healthchecksSettings !== activeSettings

    // ── Status card ──
    const renderStatusCard = () => (
        <SectionCard>
            <Grid container justifyContent={"space-between"} alignItems={"center"} spacing={2}>
                <Grid size={{xs: 12, md: 7}}>
                    <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
                        <Box
                            sx={{
                                width: 40, height: 40, borderRadius: tk.radius.md, flexShrink: 0,
                                backgroundColor: isEnabled ? tk.color.green100 : "#F3F4F6",
                                color: isEnabled ? tk.color.greenDark : tk.color.textTertiary,
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                        >
                            <MonitorHeartRounded sx={{fontSize: 22}} />
                        </Box>
                        <Box>
                            <Stack direction={"row"} spacing={1} alignItems={"center"} sx={{mb: 0.25}}>
                                <Typography sx={{fontSize: "15px", fontWeight: 700}}>
                                    {isEnabled ? t("healthchecks_active") : t("healthchecks_inactive")}
                                </Typography>
                                <Badge tone={isEnabled ? "green" : "gray"} dot>
                                    {isEnabled ? t("common:active") : t("common:inactive", {defaultValue: "Inactive"})}
                                </Badge>
                            </Stack>
                            <Typography sx={{fontSize: "13px", color: tk.color.textSecondary}}>
                                {t("healthchecks_toggle_intro")}
                            </Typography>
                        </Box>
                    </Stack>
                </Grid>
                <Grid size={{xs: 12, md: 5}} sx={{textAlign: {xs: "left", md: "right"}}}>
                    {canManage ? (
                        <>
                            {isEnabled && (
                                <Button
                                    variant={"contained"} disableElevation
                                    onClick={handleUpdateHealthchecks}
                                    sx={{mr: 1.5, textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm}}
                                    disabled={!hasChanges}
                                >
                                    {t("update_healthchecks")}
                                </Button>
                            )}
                            <Button
                                variant={isEnabled ? "outlined" : "contained"} disableElevation
                                data-step-target={"launch-healthchecks"}
                                startIcon={isEnabled ? <Close /> : !activeSettings ? <Add /> : <Check />}
                                onClick={handleToggleHealthchecks}
                                color={isEnabled ? "error" : "primary"}
                                sx={{textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm}}
                            >
                                {isEnabled ? t("deactivate") : !activeSettings ? t("create") : t("activate")}
                            </Button>
                        </>
                    ) : (
                        <Typography sx={{fontSize: "12.5px", color: tk.color.textSecondary}}>
                            {readOnlyRole ? t("healthchecks_read_only") : t("healthchecks_own_org_only")}
                        </Typography>
                    )}
                </Grid>
            </Grid>
        </SectionCard>
    )

    // ── Loading placeholder (mirrors the status card's layout) ──
    const renderLoadingCard = () => (
        <SectionCard>
            <Stack direction={"row"} spacing={1.5} alignItems={"center"} data-testid={"hc-loading"}>
                <Skeleton variant={"rounded"} width={40} height={40} sx={{borderRadius: tk.radius.md, flexShrink: 0}} />
                <Box sx={{flex: 1}}>
                    <Skeleton variant={"text"} width={180} sx={{fontSize: "15px"}} />
                    <Skeleton variant={"text"} width={280} sx={{fontSize: "13px"}} />
                </Box>
                <Skeleton variant={"rounded"} width={110} height={36} sx={{borderRadius: tk.radius.sm}} />
            </Stack>
        </SectionCard>
    )

    // ── Settings card ──
    const subLabel = (text: string) => (
        <Typography sx={{fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: tk.color.textTertiary}}>
            {text}
        </Typography>
    )

    const renderSettingsCard = () => (
        <SectionCard title={t("healthchecks_behavior")}>
            <Grid container spacing={2}>
                <Grid size={{xs: 12, md: 4}}>
                    <FormControl size={"small"} fullWidth>
                        <InputLabel id={"interval"}>{t("interval")}</InputLabel>
                        <Select label={t("interval")} labelId={"interval"} value={healthchecksSettings.interval}
                                disabled={!canManage}
                                onChange={(e) => setHealthchecksSettings({...healthchecksSettings, interval: Number(e.target.value)})}>
                            <MenuItem value={720}>{t("twice_a_day")}</MenuItem>
                            <MenuItem value={1440}>{t("once_a_day")}</MenuItem>
                            <MenuItem value={5040}>{t("twice_a_week")}</MenuItem>
                            <MenuItem value={10080}>{t("once_a_week")}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{xs: 12, md: 4}}>
                    <FormControl size={"small"} fullWidth>
                        <InputLabel id={"retries"}>{t("retries")}</InputLabel>
                        <Select label={t("retries")} labelId={"retries"} value={healthchecksSettings.retries}
                                disabled={!canManage}
                                onChange={(e) => setHealthchecksSettings({...healthchecksSettings, retries: Number(e.target.value)})}>
                            {[0, 1, 2, 3, 4, 5].map((r: number) => <MenuItem value={r} key={r}>{r}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{xs: 12, md: 4}}>
                    <FormControl size={"small"} fullWidth>
                        <InputLabel id={"retriesInterval"}>{t("retriesInterval")}</InputLabel>
                        <Select label={t("retriesInterval")} labelId={"retriesInterval"} value={healthchecksSettings.retryInterval}
                                disabled={!canManage}
                                onChange={(e) => setHealthchecksSettings({...healthchecksSettings, retryInterval: Number(e.target.value)})}>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((r: number) => <MenuItem value={r} key={r}>{r}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={12} sx={{mt: 1}}>{subLabel(t("error_checking"))}</Grid>
                <Grid size={{xs: 12, md: 4}}>
                    <FormControl fullWidth size={"small"}>
                        <InputLabel id={"errors"}>{t("healthchecks_behavior")}</InputLabel>
                        <Select value={"default"} disabled label={t("healthchecks_behavior")}>
                            <MenuItem value={"default"}>{t("default_errors")}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{xs: 12, md: 8}} alignContent={"center"}>
                    <Typography variant={"body2"} sx={{color: tk.color.textSecondary}}>{t("default_includes")}</Typography>
                </Grid>

                <Grid size={12} sx={{mt: 1}}>{subLabel(t("email_addresses"))}</Grid>
                <Grid size={12} container spacing={1.5} alignItems={"center"}>
                    <Grid size={{xs: 12, sm: 5, md: 4}}>
                        <TextField label={t("email_address")} size={"small"} fullWidth disabled={!canManage}
                                   value={pendingEmail} onChange={(e) => setPendingEmail(e.currentTarget.value)} />
                    </Grid>
                    <Grid>
                        <Button variant={"contained"} disableElevation size={"medium"} onClick={addEmailToList}
                                disabled={!pendingEmail || !canManage}
                                sx={{textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm}}>
                            {t("add_email_to_list")}
                        </Button>
                    </Grid>
                </Grid>

                {!!healthchecksSettings.notifyAddresses && healthchecksSettings.notifyAddresses.length > 0 && (
                    <Grid size={12}>
                        <Stack spacing={1} sx={{mt: 0.5}}>
                            {healthchecksSettings.notifyAddresses.map((email: string, i: number) => (
                                <Box key={i} sx={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                    px: 1.5, py: 1, borderRadius: tk.radius.sm,
                                    backgroundColor: tk.color.bg, border: `1px solid ${tk.color.borderLight}`,
                                }}>
                                    <Typography sx={{fontSize: "13px"}}>{email}</Typography>
                                    <IconButton size={"small"} color={"error"} onClick={() => removeEmailFromList(email)}
                                                disabled={healthchecksSettings.notifyAddresses.length === 1 || !canManage}>
                                        <Close fontSize={"small"} />
                                    </IconButton>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>
                )}
            </Grid>
        </SectionCard>
    )

    return (
        <Box>
            <Backdrop open={loading} sx={{zIndex: 9999, color: "#fff"}}>
                <CircularProgress color={"inherit"} />
            </Backdrop>

            <PageHeader title={t("healthchecks")} subtitle={t("healthchecks_subtitle", {defaultValue: "Monitor your QR codes and get notified on errors"})} />

            <Stack spacing={2.5}>
                {initializing ? renderLoadingCard() : renderStatusCard()}
                <Collapse in={activeHC && !initializing}>
                    {renderSettingsCard()}
                </Collapse>
            </Stack>
        </Box>
    )
}
